import { ChangeEvent, useEffect, useState } from "react";
import moment from "moment";
import {
    Chart as ChartJS,
    Tooltip,
    Legend,
    ArcElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    BarElement,
} from 'chart.js';
import { Pie, Line, Bar } from 'react-chartjs-2';

import HeaderComponent from "../../../components/header";
import ContainerComponent from "../../../components/conatiner";
import CardHeaderBackgroundComponent from "../../../components/cardHeaderBackground";
import CardInfoComponent from "../../../components/cardInfo";
import InputComponent from "../../../components/input";
import LoadingComponent from "../../../components/loading";
import { ToastErro } from "../../../components/toast";
import { ApproversDashboardService } from "../../../services/approvers/dashboard";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    ArcElement,
    Tooltip,
    Legend,
    BarElement,
);

const brl = (v: number) => (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const ApproversDashboardPage = () => {

    const [loading, setLoading] = useState<boolean>(true);
    const [startDate, setStartDate] = useState<string>(moment().startOf('month').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState<string>(moment().format('YYYY-MM-DD'));

    const [kpis, setKpis] = useState<any>({});
    const [agingPending, setAgingPending] = useState<any[]>([]);
    const [approvalsByMonth, setApprovalsByMonth] = useState<any[]>([]);
    const [approvalsByCostCenter, setApprovalsByCostCenter] = useState<any[]>([]);
    const [approvalsByType, setApprovalsByType] = useState<any[]>([]);

    const load = async () => {
        setLoading(true);
        try {
            const resp = await ApproversDashboardService(startDate, endDate);
            setKpis(resp.data.kpis || {});
            setAgingPending(resp.data.agingPending || []);
            setApprovalsByMonth(resp.data.approvalsByMonth || []);
            setApprovalsByCostCenter(resp.data.approvalsByCostCenter || []);
            setApprovalsByType(resp.data.approvalsByType || []);
        } catch (error: any) {
            ToastErro(error?.response?.data?.message || 'Erro ao carregar dashboard');
        }
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const monthLineData = {
        labels: approvalsByMonth.map((m: any) => moment(m.month + '-01').format('MMM/YY')),
        datasets: [
            {
                label: 'Aprovadas',
                data: approvalsByMonth.map((m: any) => Number(m.approved)),
                borderColor: '#00e396',
                backgroundColor: 'rgba(0,227,150,0.15)',
                tension: 0.3,
            },
            {
                label: 'Reprovadas',
                data: approvalsByMonth.map((m: any) => Number(m.reproved)),
                borderColor: '#ff4560',
                backgroundColor: 'rgba(255,69,96,0.15)',
                tension: 0.3,
            }
        ]
    };

    const ccBarData = {
        labels: approvalsByCostCenter.map((c: any) => c.cost_center),
        datasets: [
            {
                label: 'Aprovadas',
                data: approvalsByCostCenter.map((c: any) => Number(c.approved)),
                backgroundColor: '#00e396',
            },
            {
                label: 'Reprovadas',
                data: approvalsByCostCenter.map((c: any) => Number(c.reproved)),
                backgroundColor: '#ff4560',
            }
        ]
    };

    const typePieData = {
        labels: approvalsByType.map((t: any) => t.type),
        datasets: [{
            data: approvalsByType.map((t: any) => Number(t.value)),
            backgroundColor: approvalsByType.map((t: any) => t.color || '#cccccc'),
        }]
    };

    return (
        <>
            <HeaderComponent title="Dashboard Aprovador" />
            <ContainerComponent>
                <>
                    <CardHeaderBackgroundComponent title="Período">
                        <div className="row align-items-end">
                            <InputComponent
                                label="Data Inicial"
                                col={3}
                                type="date"
                                defaultValue={startDate}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value)}
                                size="small"
                                shrink
                            />
                            <InputComponent
                                label="Data Final"
                                col={3}
                                type="date"
                                defaultValue={endDate}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value)}
                                size="small"
                                shrink
                            />
                            <div className="col-md-3 mb-4">
                                <button className="btn btn-primary" type="button" onClick={load}>Atualizar</button>
                            </div>
                        </div>
                    </CardHeaderBackgroundComponent>

                    {loading ? <LoadingComponent /> : (
                        <>
                            {/* KPIs */}
                            <div className="row">
                                <CardInfoComponent col={3} icon="hourglass_empty" title="Aguardando minha aprovação" subtitle={String(kpis.pending_my_approval_count ?? 0)} />
                                <CardInfoComponent col={3} icon="payments" title="Valor pendente (meu nível)" subtitle={brl(kpis.pending_my_approval_value)} />
                                <CardInfoComponent col={3} icon="check_circle" title="Aprovadas no período" subtitle={String(kpis.approved_by_me_in_range ?? 0)} />
                                <CardInfoComponent col={3} icon="cancel" title="Reprovadas no período" subtitle={String(kpis.reproved_by_me_in_range ?? 0)} />
                            </div>
                            <div className="row">
                                <CardInfoComponent col={3} icon="warning" title="Mais antiga pendente (dias)" subtitle={kpis.oldest_pending_days !== null ? String(kpis.oldest_pending_days) : '-'} />
                                <CardInfoComponent col={3} icon="timer" title="Tempo médio p/ aprovar (dias)" subtitle={kpis.avg_days_to_approve !== null ? String(kpis.avg_days_to_approve) : '-'} />
                                <CardInfoComponent col={3} icon="domain" title="Centros de custo que aprovo" subtitle={String(kpis.total_cost_centers ?? 0)} />
                            </div>

                            {/* Evolução mensal */}
                            <div className="row gx-5">
                                <div className="col-xl-12 col-lg-12 mb-5">
                                    <div className="card card-raised h-100">
                                        <div className="card-header bg-primary text-white px-4"><div className="fw-500">Minhas aprovações — últimos 12 meses</div></div>
                                        <div className="card-body" style={{ position: 'relative', height: 320 }}>
                                            <Line
                                                data={monthLineData}
                                                options={{ responsive: true, maintainAspectRatio: false }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* CC + categoria */}
                            <div className="row gx-5">
                                <div className="col-xl-8 col-lg-8 mb-5">
                                    <div className="card card-raised h-100">
                                        <div className="card-header bg-primary text-white px-4"><div className="fw-500">Aprovações por centro de custo (período)</div></div>
                                        <div className="card-body" style={{ position: 'relative', height: 340 }}>
                                            <Bar
                                                data={ccBarData}
                                                options={{ indexAxis: 'y' as const, responsive: true, maintainAspectRatio: false }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xl-4 col-lg-4 mb-5">
                                    <div className="card card-raised h-100">
                                        <div className="card-header bg-primary text-white px-4"><div className="fw-500">Valor aprovado por categoria</div></div>
                                        <div className="card-body" style={{ position: 'relative', height: 340 }}>
                                            {approvalsByType.length > 0
                                                ? <Pie data={typePieData} options={{ responsive: true, maintainAspectRatio: false }} />
                                                : <div className="text-center text-muted mt-4">Sem dados no período</div>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Aging */}
                            <div className="row gx-5">
                                <div className="col-xl-12 col-lg-12 mb-5">
                                    <div className="card card-raised h-100">
                                        <div className="card-header bg-primary text-white px-4"><div className="fw-500">Top 10 ordens aguardando minha aprovação (mais antigas)</div></div>
                                        <div className="card-body">
                                            <div className="table-responsive">
                                                <table className="table table-sm">
                                                    <thead>
                                                        <tr>
                                                            <th>Nº</th>
                                                            <th>Usuário</th>
                                                            <th>Centro de Custo</th>
                                                            <th>Nível</th>
                                                            <th>Desde</th>
                                                            <th>Dias</th>
                                                            <th className="text-end">Valor pendente</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {agingPending.map((o: any) => (
                                                            <tr key={o.id}>
                                                                <td>{o.id}</td>
                                                                <td>{o.name}</td>
                                                                <td>{o.cost_center}</td>
                                                                <td>{o.level}</td>
                                                                <td>{o.updated_at ? moment(o.updated_at).format('DD/MM/YYYY HH:mm') : '-'}</td>
                                                                <td>{o.days_waiting !== null ? Number(o.days_waiting).toFixed(1) : '-'}</td>
                                                                <td className="text-end">{brl(Number(o.value))}</td>
                                                            </tr>
                                                        ))}
                                                        {agingPending.length === 0 && (
                                                            <tr><td colSpan={7} className="text-center text-muted">Nenhuma ordem aguardando</td></tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </>
            </ContainerComponent>
        </>
    );
};

export default ApproversDashboardPage;
